import { useState, type VoidFunctionComponent } from 'react';

import { isFilled } from '../../../../shared/utils/isFilled';
import Button from '../../generic/Button/Button';
import LoaderButton from '../../generic/Button/LoaderButton';
import FormLabel from '../../generic/Forms/FormLabel';
import LabeledTextArea from '../../generic/Forms/LabeledTextArea';
import LabeledTextInput from '../../generic/Forms/LabeledTextInput';
import LicenseOptions from '../../generic/LicenseOptions';
import MailToLink from '../../generic/Link/MailToLink';
import type { ManuallyAddedDependency, ManuallyAddedDependencyRequest } from '../../../../typings/api';

import styles from './CreateDependency.module.less';

type Props = {
  /**
   * Whether the dependency is being added as a vendored dependency.
   */
  isVendored?: boolean;
  /**
   * Locator of the existing dependency when overwriting.
   */
  overwriteDependencyLocator?: string;

  /**
   * Callback to save the dependency.
   * NOTE: The return value here is typed as "unknown" because React Query's mutateAsync
   * returns whatever the mutation function returns but we don't care about it.
   */
  saveDependency?: (params: ManuallyAddedDependencyRequest) => Promise<unknown>;
  /**
   * Whether the dependency is currently being saved.
   */
  isSaving?: boolean;

  /**
   * Callback when the drawer is closed.
   */
  onClose?: () => void;
  /**
   * Callback invoked after a successful save. Falls back to `onClose` when not provided.
   */
  onSaved?: () => void;
};

/**
 * Renders the "Create" tab of the Add Dependency drawer, allowing users to
 * create a custom dependency by entering package details manually.
 */
const CreateDependency: VoidFunctionComponent<Props> = ({
  isVendored,
  overwriteDependencyLocator,
  saveDependency,
  isSaving,
  onClose,
  onSaved,
}) => {
  const [filePath, setFilePath] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [version, setVersion] = useState<string>('');
  const [homepage, setHomepage] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  const [license, setLicense] = useState<string | undefined>(undefined);
  const [licenseText, setLicenseText] = useState<string>('');
  const [copyright, setCopyright] = useState<string>('');

  const canSaveVendored = isVendored ? isFilled(filePath) : true;
  const canSaveLicense = !!license;
  const canSave = isFilled(name) && isFilled(version) && canSaveVendored && canSaveLicense;

  const onSave = async () => {
    // NOTE: This will only ever be called if canSave is true but TS doesn't know that.
    if (!canSave) {
      return;
    }

    const newDependency: ManuallyAddedDependency = {
      kind: 'user',
      name: name.trim(),
      version: version.trim(),
      description: description.trim() || undefined,
      homepage: homepage.trim() || undefined,
      vendoredPath: filePath.trim() || undefined,
      licenses: [{
        licenseId: license,
        text: licenseText.trim() || undefined,
        copyright: copyright.trim() || undefined,
      }],
    };

    if (overwriteDependencyLocator) {
      await saveDependency?.({
        kind: 'replace',
        overwrittenLocator: overwriteDependencyLocator,
        newDependency,
      });
    } else {
      await saveDependency?.({
        kind: 'new',
        newDependency,
      });
    }

    (onSaved ?? onClose)?.();
  };

  return (
    <section>
      <header>
        <p className={styles.overview}>
          Create a custom {isVendored ? 'vendored' : 'manual'} dependency when the package can&apos;t be found or validated.
        </p>
      </header>

      <main className={styles.main}>
        <h3 className={styles.title}>
          Package Details
        </h3>
        {isVendored && (
          <div className={styles.field}>
            <LabeledTextInput
              id="create-dependency-file-path"
              size="small"
              label="File Path"
              placeholder="Path to the vendored dependency"
              value={filePath}
              onChange={(value) => setFilePath(value)}
              isDisabled={isSaving}
              isRequired
            />
            <p className={styles.notes}>Add the path to the vendored dependency.</p>
          </div>
        )}

        <div className={styles.field}>
          <LabeledTextInput
            id="create-dependency-name"
            size="small"
            label="Name"
            placeholder="Enter a package name"
            value={name}
            onChange={(value) => setName(value)}
            isDisabled={isSaving}
            isRequired
          />
        </div>

        <div className={styles.field}>
          <LabeledTextInput
            id="create-dependency-version"
            size="small"
            label="Version"
            placeholder="Enter a version number, eg. v1 or 1.0"
            value={version}
            onChange={(value) => setVersion(value)}
            isDisabled={isSaving}
            isRequired
          />
        </div>

        <div className={styles.field}>
          <LabeledTextInput
            id="create-dependency-homepage"
            size="small"
            label="Homepage"
            placeholder="https://dependency.com"
            value={homepage}
            onChange={(value) => setHomepage(value)}
            isDisabled={isSaving}
          />
        </div>

        <div className={styles.field}>
          <LabeledTextArea
            id="create-dependency-description"
            size="small"
            label="Description"
            placeholder="Enter a description"
            value={description}
            onChange={(value) => setDescription(value)}
            isDisabled={isSaving}
          />
        </div>

        <h3 className={styles.title}>
          License Details
        </h3>

        <div className={styles.field}>
          <FormLabel label="License" requiredText="(Required)">
            <LicenseOptions selectedLicenseId={license} onChange={setLicense} />
          </FormLabel>
        </div>

        <div className={styles.field}>
          <LabeledTextArea
            id="create-dependency-license-text"
            size="small"
            label="Raw License Text"
            placeholder="Enter license text"
            value={licenseText}
            onChange={(value) => setLicenseText(value)}
            isDisabled={isSaving}
          />
        </div>

        <div className={styles.field}>
          <LabeledTextArea
            id="create-dependency-copyright"
            size="small"
            label="Copyright"
            placeholder="Enter copyright text"
            value={copyright}
            onChange={(value) => setCopyright(value)}
            isDisabled={isSaving}
          />
        </div>

        <p className={styles.support}>Can&apos;t find the license you&apos;re looking for? <MailToLink email="support@fossa.com">Contact support</MailToLink> to request it be added.</p>

        <div className={styles.infoCard}>
          <p className={styles.infoCardHeading}>Custom dependencies aren&apos;t validated by FOSSA</p>
          <p className={styles.infoCardText}>
            They don&apos;t map to a known package source, so package details are saved as provided.
          </p>
        </div>
      </main>

      <footer className={styles.footer}>
        <Button type="secondary" onClick={() => onClose?.()}>Cancel</Button>
        <LoaderButton
          type="primary"
          onClick={onSave}
          isDisabled={!canSave || isSaving}
          isLoading={isSaving}
        >
          Create
        </LoaderButton>
      </footer>
    </section>
  );
};

export default CreateDependency;
